import { useCallback, useEffect, useRef } from 'react';
import { CompanyService, RetrieveDocumentGenerationJob } from '../../api';
import { USERS } from '../../common/endpoints';
import { useStore } from '../../common/store';
import { UseStoreValues } from '../../common/types/store';
import { useEventBus } from '../../context';
import { useAsyncProcessor } from '../../context/AsyncProcessorContext/AsyncProcessorContext';
import { clientWithAuth } from '../../dashboard409a/services/base';
import { hasStoredAccessToken } from '../../pages/Auth/utils';
import { Logger } from '../../utilities';
import { createCapTableGenerationOnResult } from '../capTableGenerationResult';
import CompaniesService from '../companies';
import { CAP_TABLE_GENERATION_JOB_TITLE } from '../generationJobs';

export enum POLL_TYPE {
  GENERATION_JOBS = 'generation_jobs',
}

const POLLING_INTERVAL_MS = 5000;

const TERMINAL_STATUSES = new Set([
  RetrieveDocumentGenerationJob.status.COMPLETED,
  RetrieveDocumentGenerationJob.status.FAILED,
]);

/**
 * Fetch the user's generation jobs from the user-scoped endpoint.
 * The backend syncs each job with the external service in this request,
 * so one call is sufficient to update statuses and fire notifications.
 *
 * Jobs that transition to a terminal state during the sync are still
 * returned (with the updated status) - they won't appear on the next call.
 */
const fetchActiveJobs = async (): Promise<RetrieveDocumentGenerationJob[]> => {
  const url = USERS.getUserGenerationJobs(true);
  const response = await clientWithAuth.get<RetrieveDocumentGenerationJob[]>(url);
  return response.data ?? [];
};

const COMPLETED_JOBS_PAGE_SIZE = 50;

// Both cap-table job flavors poll the same job endpoint and apply results
// through the same cap-table apply endpoint, so the resume and registration
// paths treat them identically.
const CAP_TABLE_PROCESSING_TYPES = [
  RetrieveDocumentGenerationJob.processing_type.CAP_TABLE,
  RetrieveDocumentGenerationJob.processing_type.CAP_TABLE_CITATIONS_ONLY,
];

// Retry schedule for a failed initialization burst: up to three timed retries
// with growing delays. Once spent, recovery is left to the external triggers
// (reload, company switch, manual poll).
const INIT_RETRY_DELAYS_MS = [5000, 15000, 45000];

/**
 * Fetch every COMPLETED job of one processing type for a company, paging past
 * the backend's default page size (10). Terminal-status queries are served
 * straight from the local DB with exact page slices, so a short page marks the
 * end of the set.
 */
const fetchAllCompletedJobs = async (
  companiesService: CompaniesService,
  companyId: string,
  processingType: RetrieveDocumentGenerationJob.processing_type
): Promise<RetrieveDocumentGenerationJob[]> => {
  const jobs: RetrieveDocumentGenerationJob[] = [];
  for (let page = 0; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pageJobs = (await companiesService.getDocumentGenerationJobs(
      companyId,
      page,
      COMPLETED_JOBS_PAGE_SIZE,
      RetrieveDocumentGenerationJob.status.COMPLETED,
      processingType
    )) as RetrieveDocumentGenerationJob[];
    jobs.push(...pageJobs);
    if (pageJobs.length < COMPLETED_JOBS_PAGE_SIZE) {
      return jobs;
    }
  }
};

/**
 * Hook that polls all active generation jobs for the authenticated user.
 *
 * Serves two purposes:
 * 1. Polling loop via `GET /api/users/me/document-generation-jobs?active_only=true`:
 *    triggers backend status sync, Ably publishes updates, the notification center
 *    picks them up. Terminal side effects for FINANCIAL_MAPPING (attribute mapping
 *    creation) are handled by the Ably `onJobCompleted` callback in FirmBaseLayout - not here.
 *
 * 2. AsyncProcessorContext per-job registration: initializes per-job polling for active
 *    FINANCIAL_MAPPING and cap-table jobs (both cap-table processing types) on the current
 *    company, enabling UI progress tracking. Cap-table side effects (applying results to
 *    the cap table) are handled here via `onResult`. Cap-table jobs that finished while no
 *    client was alive (COMPLETED without the applied_at stamp the backend writes on apply)
 *    are applied directly, without any polling registration.
 */
export const useAppLoadAsyncOperations = (initOnLoad = true) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // job_ids whose COMPLETED result was already handed to the apply handler this
  // session - one apply per job even when it surfaces through both the per-job
  // polling and the completed-jobs resume fetch.
  const appliedCapTableJobIdsRef = useRef(new Set<string>());
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  // Key everything on the id, not the companyInfo object: the store publishes
  // new companyInfo identities for the same company during hydration.
  const companyId = storeValue.companyInfo?.id;
  const lastInitializedCompanyIdRef = useRef<typeof companyId | null>(null);
  const { publish } = useEventBus();

  // Ably-trigger polling loop: polls the user-scoped endpoint to trigger backend
  // sync. Ably then fires status-change events that the notification center consumes.

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (): Promise<boolean> => {
    try {
      const jobs = await fetchActiveJobs();
      return jobs.some(j => !TERMINAL_STATUSES.has(j.status!));
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Polling error', error);
      return false;
    }
  }, []);

  const startPolling = useCallback(async () => {
    if (intervalRef.current) {
      return;
    }

    // The shell mounts unconditionally, including on the login screen. Skip the
    // poll until there is a stored access token so we never fire an
    // unauthenticated GET (which logs a 401 console error on the login page).
    if (!hasStoredAccessToken()) {
      return;
    }

    const hasActive = await pollOnce();
    if (!hasActive) {
      return;
    }

    Logger.info('useAppLoadAsyncActions:', 'Active generation jobs found, starting poll');
    intervalRef.current = setInterval(async () => {
      const stillActive = await pollOnce();
      if (!stillActive) {
        stopPolling();
      }
    }, POLLING_INTERVAL_MS);
  }, [pollOnce, stopPolling]);

  // AsyncProcessorContext per-job registration: registers active FINANCIAL_MAPPING
  // and cap-table jobs for individual polling and UI progress tracking.
  // FINANCIAL_MAPPING side effects (attribute mapping creation) are intentionally
  // omitted here - handled by the Ably onJobCompleted callback in FirmBaseLayout.

  /** Resolves false when a request failed, so the mount effect can schedule a timed retry. */
  const initializeGenerationJobs = useCallback(async (): Promise<boolean> => {
    if (!companyId) {
      return true;
    }
    const companyIdStr = companyId.toString();
    const companiesService = new CompaniesService();
    try {
      const capTableOnResult = createCapTableGenerationOnResult({
        companyId: companyIdStr,
        logContext: 'useAppLoadAsyncActions',
        publish,
        removeJob,
        // A failed apply never stamped applied_at on the backend, so
        // unlatch the once-guard: the next initialization run will see the job
        // still unapplied and retry it instead of dropping it for the session.
        onApplyError: result => appliedCapTableJobIdsRef.current.delete(result.job_id),
      });
      const applyCapTableResultOnce = async (result: RetrieveDocumentGenerationJob) => {
        if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
          if (appliedCapTableJobIdsRef.current.has(result.job_id)) {
            // Already applied this session: clear any registered poller so the
            // job cannot keep polling forever behind the once-guard.
            removeJob(result.job_id);
            return;
          }
          // Latch before the await so a job surfacing concurrently through both
          // paths still applies once; onApplyError above unlatches on failure.
          appliedCapTableJobIdsRef.current.add(result.job_id);
        }
        await capTableOnResult(result);
      };

      // TODO: update the API here to enable OR searching...
      // The API filters on one status + processing type per request, so fetch
      // each PENDING/IN_PROGRESS combination in parallel.
      const fetchActiveJobsFor = (processingType: RetrieveDocumentGenerationJob.processing_type) =>
        Promise.all(
          [RetrieveDocumentGenerationJob.status.PENDING, RetrieveDocumentGenerationJob.status.IN_PROGRESS].map(
            status =>
              companiesService.getDocumentGenerationJobs(
                companyIdStr,
                undefined,
                undefined,
                status,
                processingType
              ) as Promise<RetrieveDocumentGenerationJob[]>
          )
        ).then(lists => lists.flat());

      const [activeFinMapJobs, ...activeCapTableLists] = await Promise.all([
        fetchActiveJobsFor(RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING),
        ...CAP_TABLE_PROCESSING_TYPES.map(fetchActiveJobsFor),
      ]);

      // Each list request above also syncs job statuses on the backend, which
      // can flip a PENDING job to COMPLETED mid-request. Querying COMPLETED
      // cap-table jobs only after those refreshes settle guarantees a job that
      // transitions during the refresh is seen here instead of falling between
      // the two queries.
      const completedCapTableJobs = (
        await Promise.all(
          CAP_TABLE_PROCESSING_TYPES.map(type => fetchAllCompletedJobs(companiesService, companyIdStr, type))
        )
      ).flat();

      const activeCapTableJobs = activeCapTableLists.flat();
      const unappliedCapTableJobs = completedCapTableJobs.filter(
        // Cast: the generated job type has no applied_at yet (codegen drift).
        job => !(job as RetrieveDocumentGenerationJob & { applied_at?: string | null }).applied_at
      );

      if (activeFinMapJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active FINANCIAL_MAPPING jobs to poll', activeFinMapJobs.length);
      }
      if (activeCapTableJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active cap-table jobs to poll', activeCapTableJobs.length);
      }
      if (unappliedCapTableJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found completed unapplied cap-table jobs to apply', unappliedCapTableJobs.length);
      }

      // Jobs completed while no client was alive (e.g. by the scheduled
      // reconciler) are already terminal: apply them now, no polling needed.
      // Oldest first (the endpoint returns newest first), one at a time, so
      // overlapping writes to the same cap table stay deterministic.
      const resumedJobs = [...unappliedCapTableJobs].sort(
        (a, b) => new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime()
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const job of resumedJobs) {
        // eslint-disable-next-line no-await-in-loop
        await applyCapTableResultOnce(job);
      }

      // Register FINANCIAL_MAPPING jobs for individual polling. Mapping creation
      // side effect is handled by the Ably onJobCompleted callback in FirmBaseLayout.
      activeFinMapJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Financial Mapping',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyIdStr,
                job.job_id
              );
              return result;
            },
            pollingInterval: 5000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (TERMINAL_STATUSES.has(result.status!)) {
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Generation job polling error', error);
          },
        });
      });

      // Register cap-table jobs - side effects handled here (not yet via Ably).
      // Skip ids the resume pass above already applied (a list sync can flip a
      // job to COMPLETED mid-burst, landing it in both the active and completed
      // sets); registering one would leave its poller ticking forever.
      activeCapTableJobs
        .filter(job => !appliedCapTableJobIdsRef.current.has(job.job_id))
        .forEach((job: RetrieveDocumentGenerationJob) => {
          addJob({
            id: job.job_id,
            companyId: companyIdStr,
            details: {
              title: CAP_TABLE_GENERATION_JOB_TITLE,
              description: `Processing job ${job.job_id}`,
            },
            pollingConfig: {
              pollingFunction: async () => {
                const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                  companyIdStr,
                  job.job_id
                );
                return result;
              },
              pollingInterval: 3000,
              pollingArgs: [],
            },
            onResult: applyCapTableResultOnce,
            onError: (error: Error) => {
              Logger.error('useAppLoadAsyncActions - Cap table generation job polling error', error);
            },
          });
        });
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Error initializing jobs from API', error);
      return false;
    }
    return true;
  }, [companyId, addJob, removeJob, publish]);

  const manuallyExecutePoll = useCallback(
    (pollType: POLL_TYPE.GENERATION_JOBS) => {
      if (pollType === POLL_TYPE.GENERATION_JOBS) {
        startPolling();
        initializeGenerationJobs();
      }
    },
    [startPolling, initializeGenerationJobs]
  );

  useEffect(() => {
    // One poll start + job-fetch burst per company id; the manual re-poll path
    // above stays unguarded on purpose. A failed burst retries itself on a
    // timer, once per delay in INIT_RETRY_DELAYS_MS; a spent budget hands
    // recovery back to the external triggers (reload, company switch, manual
    // poll), so a persistent failure never becomes a hot loop.
    if (!initOnLoad || lastInitializedCompanyIdRef.current === companyId) {
      return stopPolling;
    }
    // Latch up front so overlapping runs cannot double-fetch. The cleanup
    // unlatches unless a burst succeeded, so an interrupted run (company
    // switch, unmount) never counts as initialized.
    lastInitializedCompanyIdRef.current = companyId;
    startPolling();

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let succeeded = false;

    const attemptInit = (attempt: number) => {
      initializeGenerationJobs().then(initialized => {
        if (cancelled) {
          return;
        }
        if (initialized) {
          succeeded = true;
        } else if (attempt < INIT_RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => attemptInit(attempt + 1), INIT_RETRY_DELAYS_MS[attempt]);
        } else {
          lastInitializedCompanyIdRef.current = null;
        }
      });
    };
    attemptInit(0);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (!succeeded && lastInitializedCompanyIdRef.current === companyId) {
        lastInitializedCompanyIdRef.current = null;
      }
      stopPolling();
    };
  }, [initOnLoad, companyId, startPolling, stopPolling, initializeGenerationJobs]);

  return { manuallyExecutePoll };
};

export default useAppLoadAsyncOperations;
